// components/MessageItem.jsx
import {
  Box,
  Paper,
  Typography,
  Stack,
  Avatar,
  IconButton,
} from '@mui/material';
import { Reply } from '@mui/icons-material';
import ImageDisplay from './ImageDisplay';
import { useColorScheme } from '@mui/material/styles';
import { handleMentionClick, formatUTCToLocalTime } from '../utils/constants';

const MessageItem = ({ message, currentUser, onReply }) => {
  const isOwn = currentUser && message.user_id === currentUser.id;
  const { mode } = useColorScheme();
  let pressTimer;
  const handleTouchStart = () => {
    pressTimer = setTimeout(() => {
      handleMentionClick(message.reply_to_id); // 長按觸發
    }, 500); // 0.5 秒
  };
  const handleTouchEnd = () => {
    clearTimeout(pressTimer);
  };

  return (
    <Box
      id={`message-${message.id}`}
      sx={{
        display: 'flex',
        justifyContent: isOwn ? 'flex-end' : 'flex-start',
        mb: 1.5,
        px: 1,
      }}
    >
      <Box
        sx={{
          maxWidth: '75%',
          display: 'flex',
          flexDirection: isOwn ? 'row-reverse' : 'row',
          alignItems: 'flex-start',
          gap: 1,
        }}
      >
        <Avatar
          sx={{
            width: 32,
            height: 32,
            bgcolor: isOwn ? 'success.main' : 'primary.main',
            fontSize: '0.875rem',
          }}
        >
          {message.username.charAt(0).toUpperCase()}
        </Avatar>
        
        <Paper
          sx={{
            backgroundColor: isOwn ? 'success.main' : 'primary.main',
            color: mode === 'light' ? 'black' : 'white',
            borderRadius: 2,
            p: 1.5,
            position: 'relative',
            maxWidth: '100%',
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
            <Typography 
              variant="caption" 
              sx={{ opacity: 0.8, fontSize: '0.9rem' /* 原本 0.6rem 左右 → 放大 */ }}
            >
              {message.username}
            </Typography>
            <Typography 
              variant="caption" 
              sx={{ opacity: 0.6, fontSize: '0.75rem' /* 原本 0.5rem → 放大 */ }}
            >
              {formatUTCToLocalTime(message.created_at)}
            </Typography>
          </Stack>
          
          {message.reply_to_message && (
            <Paper
              sx={{
                backgroundColor: 'rgba(255,255,255,0.15)',
                borderRadius: 1,
                p: 1,
                mb: 1,
                borderLeft: '3px solid rgba(255,255,255,0.4)',
              }}
              onDoubleClick={() => handleMentionClick(message.reply_to_id)}
              onTouchStart={handleTouchStart}
              onTouchEnd={handleTouchEnd}
            >
              <Typography 
                variant="caption" 
                sx={{ opacity: 0.8, fontStyle: 'italic', fontSize: '0.8rem' }}
              >
                回覆: {message.reply_to_message}
              </Typography>
            </Paper>
          )}
          
          {message.message_text && (
            <Typography 
              variant="body1" 
              sx={{ 
                wordBreak: 'break-word',
                whiteSpace: 'pre-wrap',
                lineHeight: 1.3,
                fontSize: '1rem',
              }}
            >
              {message.message_text}
            </Typography>
          )}
          
          {message.images && message.images.length > 0 && (
            <Stack direction="row" spacing={0.5} sx={{ mt: 1, flexWrap: 'wrap' }}>
              {message.images.map((img, index) => (
                <ImageDisplay
                  key={index}
                  imageUrl={img}
                  alt={`圖片${index + 1}`}
                  maxWidth={200}
                  maxHeight={150}
                  onClick={(url) => window.open(url, '_blank')}
                />
              ))}
            </Stack>
          )}
          
          {!isOwn && (
            <IconButton
              size="small"
              sx={{ 
                mt: 0,
                color: 'rgba(255,255,255,0.7)',
                '&:hover': { color: 'rgba(255,255,255,0.9)' },
                transform: 'scale(0.7)'  // ✅ Reply 縮小 30%
              }}
              onClick={() => onReply(message)}
            >
              <Reply fontSize="small" />
            </IconButton>
          )}
        </Paper>
      </Box>
    </Box>
  );
};

export default MessageItem;
